/* eslint-disable import/no-cycle */
import type {
  LearnerFeedbackAttributes,
  SeriesAttributes,
  SessionAttributes,
  SessionFeedbackAutomatedAttributes,
  SessionHostAttributes,
  SessionTemplateAttributes,
  TopicAttributes,
  TutorAuditAttributes,
  UserAttributes,
} from '@/server/models'
import type { TutorSessionFeedbackAttributes } from '@/server/models/SessionFeedback'

import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import SessionBreakoutQ from '@/queries/SessionBreakoutQ'
import type { UserContextType } from '@/client/context/user'
import { SQL, type SQLFragment } from '@/client/query'
import type { LearnerFeedbackType } from '@/client/services/learnerFeedback/types'
import { ZOOM_DATA_RETENTION_DAYS } from '@/client/services/session/constants'
import type {
  SessionQualityMetricSlug,
  SessionQualityMetricSlugToPayload,
} from '@/client/services/session/qualityMetric/types'
import { PEER_REVIEW_RECENT_SESSION_THRESHOLD_DAYS } from '@/client/services/sessionAudit/constants'
import { getAuditorTagsUserCanAccess } from '@/client/services/sessionAudit/tags'
import { TutorAuditorLevel } from '@/client/services/sessionAudit/types'
import { AUDITOR_TAGS, MENTOR_TAGS, SAFETY_TAGS } from '@/client/services/tags/constants'
import { SessionTag as SessionTagType } from '@/client/services/tags/types'
import {
  EXCLUDED_SESSION_TOPICS,
  EXCLUDED_WORLD_SLUGS,
} from '@/client/services/volunteerActivity/constants'
import { DIALOGUES_WORLD_SLUG } from '@/client/services/worlds/constants'
import type { WorldVisibility } from '@/client/services/worlds/types'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOffsetToSQL,
  queryOrderToSQL,
} from './common'
import {
  ENRICHMENT_TOPIC,
  type GatingGroup,
  type HostRole,
  SESSION_FEEDBACK_TYPE_DIALOGUES,
  SESSION_FEEDBACK_TYPE_TUTOR,
  type SessionAttendancePolicy,
  SessionAttendeeRole,
  type SessionFeedbackType,
  SessionMentorStatus,
  TOPICS_TO_OMIT_FROM_PEER_REVIEWS,
  type TutorSessionFeedbackRating,
  type ViewpointSettings,
} from './constants'
import PlatformFeedbackQ from './PlatformFeedbackQ'
import SATSeriesQ from './SATSeriesQ'
import SeriesQ from './SeriesQ'
import SeriesRegistrationQ from './SeriesRegistrationQ'
import SeriesReservationQ from './SeriesReservationQ'
import SeriesTemplateQ from './SeriesTemplateQ'
import SessionAttendeeQ from './SessionAttendeeQ'
import SessionCommentQ from './SessionCommentQ'
import SessionFeedbackAutomatedQ from './SessionFeedbackAutomatedQ'
import SessionFeedbackQ from './SessionFeedbackQ'
import SessionHostQ from './SessionHostQ'
import SessionRegistrationQ from './SessionRegistrationQ'
import SessionTagQ from './SessionTagQ'
import SessionTemplateQ from './SessionTemplateQ'
import TopicQ from './TopicQ'
import TutorAuditQ from './TutorAuditQ'
import UserQ from './UserQ'
import WebsiteSettingsQ from './WebsiteSettingsQ'
import WorldMemberQ from './WorldMemberQ'
import WorldQ from './WorldQ'

const BREAKOUT_ROOM_START_DATE = SQL`'2025-04-04'::date`

const SessionQ = {
  ...makeQueryModelV2<SessionAttributes>('session')([
    'id',
    'user_id',
    'world_id',
    'description',
    'questionnaire',
    'qualifications',
    'max_learners',
    'zoom_meeting_id',
    'zoom_recording_urls',
    'topic_id',
    'series_id',
    'start_date',
    'end_date',
    'zoom_start_date',
    'zoom_end_date',
    'languages',
    'changelog',
    'mentor_actions',
    'attempted_hosting',
    'created_at',
    'updated_at',
    'deleted_at',
    'transcript_url',
    'safety_check_at',
    'safety_check_by_user_id',
    'internal_state',
    'title',
    'allow_cohosts',
    'attendance_policy',
    'lesson_prep_completed',
    'lesson_prep_response_at',
    'session_template_id',
    'safety_summary_automated',
    'safety_risk_automated',
    'quality_metrics',
    'deleted_by_user_id',
    'is_breakout_enabled',
  ]),

  associations: (): AssociationDefinitions => ({
    // Include soft-deleted users by default, since we keep Session
    // rows even after associated user is deleted.
    User: { query: UserQ, foreignKey: UserQ.id, sourceKey: SessionQ.user_id, paranoid: false },
    Hosts: { query: SessionHostQ, foreignKey: SessionHostQ.session_id, sourceKey: SessionQ.id },
    Topic: { query: TopicQ, foreignKey: TopicQ.id, sourceKey: SessionQ.topic_id },
    SessionRegistrations: {
      query: SessionRegistrationQ,
      foreignKey: SessionRegistrationQ.session_id,
      sourceKey: SessionQ.id,
    },
    Series: { query: SeriesQ, foreignKey: SeriesQ.id, sourceKey: SessionQ.series_id },
    World: { query: WorldQ, foreignKey: WorldQ.id, sourceKey: SessionQ.world_id },
    SessionBreakouts: {
      query: SessionBreakoutQ,
      foreignKey: SessionBreakoutQ.session_id,
      sourceKey: SessionQ.id,
    },
    SessionAttendees: {
      query: SessionAttendeeQ,
      foreignKey: SessionAttendeeQ.session_id,
      sourceKey: SessionQ.id,
    },
    SessionFeedbackAutomated: {
      query: SessionFeedbackAutomatedQ,
      foreignKey: SessionFeedbackAutomatedQ.session_id,
      sourceKey: SessionQ.id,
    },
    SessionFeedback: {
      query: SessionFeedbackQ,
      foreignKey: SessionFeedbackQ.session_id,
      sourceKey: SessionQ.id,
    },
    SessionTemplate: {
      query: SessionTemplateQ,
      foreignKey: SessionTemplateQ.id,
      sourceKey: SessionQ.session_template_id,
    },
  }),

  where: (
    {
      id,
      tutor,
      topicId,
      seriesId,
      worldId,
      attemptedHosting,
      attendancePolicy,
      hideGatedSessions = false,
      paranoid = true,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
      offset = null,
    }: {
      id?: number | null | SQLFragment<number>
      tutor?: number | null | SQLFragment<number>
      topicId?: number | null | SQLFragment<number>
      seriesId?: number | null | SQLFragment<number>
      worldId?: number | null | SQLFragment<number>
      attemptedHosting?: boolean | null
      attendancePolicy?: SessionAttendancePolicy | null
      hideGatedSessions?: boolean
      paranoid?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
      offset?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionAttributes> => {
    const includeToUse = include
    if (hideGatedSessions && !includeToUse.some(i => i.association === 'Series')) {
      includeToUse.push({ association: 'Series', required: false })
    }

    return SQL`
      FROM ${SessionQ}
      ${includeAssociationsToSql(SessionQ, includeToUse)}
      ${
        tutor != null
          ? SQL`JOIN ${SessionHostQ} ON ${SessionHostQ.session_id} = ${SessionQ.id}`
          : SQL``
      }
      WHERE
          ${id != null ? SQL`${SessionQ.id} = ${id} AND` : SQL``}
          ${worldId != null ? SQL`${SessionQ.world_id} = ${worldId} AND` : SQL``}
          ${
            tutor != null
              ? SQL`${SessionHostQ.user_id} = ${tutor} AND ${SessionHostQ.isApproved()} AND ${
                  SessionHostQ.deleted_at
                } IS NULL AND`
              : SQL``
          }
          ${topicId != null ? SQL`${SessionQ.topic_id} = ${topicId} AND` : SQL``}
          ${seriesId != null ? SQL`${SessionQ.series_id} = ${seriesId} AND` : SQL``}
          ${
            attemptedHosting != null
              ? SQL`${SessionQ.attempted_hosting} = ${attemptedHosting} AND`
              : SQL``
          }
          ${
            attendancePolicy != null
              ? SQL`${SessionQ.attendance_policy} = ${attendancePolicy} AND`
              : SQL``
          }
          ${
            hideGatedSessions
              ? SQL`${SessionQ.getGatingGroup({ skipSeriesInclude: true })} IS NULL AND`
              : SQL``
          }
          ${paranoid ? SQL`${SessionQ.deleted_at} IS NULL AND` : SQL``}
          ${customWhere != null ? customWhere : SQL`TRUE`}
      ${queryGroupByToSQL(groupBy)}
      ${queryOrderToSQL(order)}
      ${queryLimitToSQL(limit)}
      ${queryOffsetToSQL(offset)}
  `
  },

  countReschedules: ({
    minTimestampQuery,
  }: {
    minTimestampQuery?: SQLFragment<Date> | null
  }): SQLFragment<number> => SQL`
    (SELECT COUNT(*)
      FROM jsonb_array_elements(${SessionQ.changelog}) AS changelog_element
      WHERE changelog_element->'before'->>'start_date' IS NOT NULL
      AND ${
        minTimestampQuery == null
          ? SQL`TRUE`
          : SQL`(changelog_element->>'timestamp')::timestamp >= ${minTimestampQuery}`
      }
    )`,

  hasEnded: (): SQLFragment<boolean> => SQL`(${SessionQ.getComputedEndDate()} <= NOW())`,

  getMentorStatus: (): SQLFragment<SessionMentorStatus> => SQL`
        CASE
            WHEN jsonb_array_length(${SessionQ.mentor_actions}) = 0 THEN ${SessionMentorStatus.None}
            ELSE ${SessionQ.mentor_actions}->-1->>'status'
        END`,

  getActionMentorUserId: (): SQLFragment<number> => SQL`
        CASE
            WHEN jsonb_array_length(${SessionQ.mentor_actions}) = 0 THEN NULL
            WHEN ${SessionQ.mentor_actions}->-1->>'status' = ${SessionMentorStatus.NeedsMentor} THEN NULL
            ELSE CAST(${SessionQ.mentor_actions}->-1->>'userId' as INT)
        END`,

  getTimestampOfFirstMentorActionWithStatus: ({
    status,
  }: {
    status: SessionMentorStatus
  }): SQLFragment<Date> => SQL`
    first_action_timestamp.timestamp FROM (
        SELECT CAST(action->>'timestamp' AS TIMESTAMP) AS timestamp FROM jsonb_array_elements(${SessionQ.mentor_actions}) AS action
            WHERE action->>'status' = ${status}
            ORDER BY action->>'timestamp' ASC
        LIMIT 1
    ) AS first_action_timestamp
  `,

  getClaimTime: (): SQLFragment<string> => SQL`
    LEAST((SELECT ${SessionQ.getTimestampOfFirstMentorActionWithStatus({
      status: SessionMentorStatus.InProgress,
    })}), (SELECT ${SessionQ.getTimestampOfFirstMentorActionWithStatus({
      status: SessionMentorStatus.Resolved,
    })})) - ${SessionQ.start_date}`,

  getResolveTime: (): SQLFragment<string> => SQL`
    (SELECT ${SessionQ.getTimestampOfFirstMentorActionWithStatus({
      status: SessionMentorStatus.Resolved,
    })}) - ${SessionQ.start_date}`,

  getAverageClaimTimeDays: ({
    earliestDate,
    latestDate,
  }: {
    earliestDate?: SQLFragment<Date>
    latestDate?: SQLFragment<Date>
  }): SQLFragment<number> =>
    SQL`EXTRACT(DAY FROM AVG(${SessionQ.getClaimTime()}))
      ${SessionQ.where(
        {},
        SQL`
          ${earliestDate != null ? SQL`${SessionQ.start_date} >= ${earliestDate}` : SQL`TRUE`} AND
          ${latestDate != null ? SQL`${SessionQ.start_date} <= ${latestDate}` : SQL`TRUE`}`
      )}`,

  getAverageResolveTimeDays: ({
    earliestDate,
    latestDate,
  }: {
    earliestDate?: SQLFragment<Date>
    latestDate?: SQLFragment<Date>
  }): SQLFragment<number> =>
    SQL`EXTRACT(DAY FROM AVG(${SessionQ.getResolveTime()}))
          ${SessionQ.where(
            {},
            SQL`
              ${earliestDate != null ? SQL`${SessionQ.start_date} >= ${earliestDate}` : SQL`TRUE`} AND
              ${latestDate != null ? SQL`${SessionQ.start_date} <= ${latestDate}` : SQL`TRUE`}`
          )}`,

  getFeaturedSessions: (): SQLFragment<SessionAttributes[]> =>
    SQL`${SessionQ.where({})} AND ${SessionQ.id} IN (
            SELECT ${WebsiteSettingsQ.getFeaturedSessions()}
        )`,

  isNotSeriesSession: (): SQLFragment<boolean> => SQL`${SessionQ.series_id} IS NULL`,
  countSessionRegistration: (): SQLFragment<number> =>
    SQL`count(*)::integer ${SessionRegistrationQ.where({
      sessionId: SessionQ.id,
    })}`,

  hasRoomForLearners: (): SQLFragment<boolean> =>
    SQL`(${SessionQ.max_learners} > (SELECT ${SessionQ.countSessionRegistration()}))`,

  isFull: (): SQLFragment<boolean> => SQL`(NOT ${SessionQ.hasRoomForLearners()})`,

  hasAnyTags: (tags: SessionTagType[], customWhere?: SQLFragment<boolean>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionTagQ.where(
      { sessionId: SessionQ.id },
      SQL`${SessionTagQ.hasAnyTag(tags)} AND (${customWhere != null ? customWhere : SQL`TRUE`})`
    )})`,

  hasTag: (tag: SessionTagType): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionTagQ.where({ sessionId: SessionQ.id, tag })})`,

  hasMentorTag: (): SQLFragment<boolean> => SessionQ.hasAnyTags(MENTOR_TAGS),

  hasSafetyTag: (): SQLFragment<boolean> => SessionQ.hasAnyTags(SAFETY_TAGS),

  hasRelevantAuditTag: (customWhere?: SQLFragment<boolean>): SQLFragment<boolean> =>
    SessionQ.hasAnyTags(
      [...AUDITOR_TAGS],
      // Only match tags from series template, if specified.
      SQL` ${SessionTagQ.tag} = ANY(
      COALESCE(
        (SELECT ${SeriesTemplateQ.peer_review_tags}
          ${SeriesQ.where({
            id: SessionQ.series_id,
            include: [
              {
                association: 'SeriesTemplate',
                required: false,
              },
            ],
          })}
        ),
        ${[...AUDITOR_TAGS]})
      ) AND ${customWhere == null ? SQL`TRUE` : customWhere}`
    ),

  hasRelevantAuditorTagForUser: (user: UserContextType): SQLFragment<boolean> =>
    SQL`${SessionQ.hasRelevantAuditTag(SessionTagQ.hasAnyTag(getAuditorTagsUserCanAccess(user)))}`,

  hasBeenAudited: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${TutorAuditQ.where(
      {
        sessionId: SessionQ.id,
      },
      SQL`(${TutorAuditQ.auditor_id} != ${SessionQ.user_id})`
    )})`,

  hasHadExperiencedAudit: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${TutorAuditQ.where(
      {
        sessionId: SessionQ.id,
      },
      SQL`
      (
        ${TutorAuditQ.isExperienced()} OR
        NOT (${TutorAuditQ.isUsingAuditorRubricVersion()})
      )
      AND (${TutorAuditQ.auditor_id} != ${SessionQ.user_id})`
    )})`,

  needsAudit: (): SQLFragment<boolean> =>
    SQL`
    (
      ${SessionQ.needsExperiencedAudit()} AND
      NOT ${SessionQ.hasBeenAudited()}
    )`,

  // An "experienced" peer reviewer is a non-novice level peer reviewer. Sessions peer reviewed by novice peer reviewers are reviewed again by a more experienced peer reviewer.
  needsExperiencedAudit: (): SQLFragment<boolean> =>
    SQL`
    (
      NOT ${SessionQ.hasHadExperiencedAudit()} AND
      ${SessionQ.isWithinZoomExpirationPeriod()} AND
      CARDINALITY(${SessionQ.zoom_recording_urls}) > 0 AND
      ${SessionQ.hasAtLeastOneLearner()} AND
      NOT ${SessionQ.hasMentorTag()} AND
      NOT ${SessionQ.hasAssociatedSafetyReport()} AND
      (
        EXISTS(SELECT 1 ${TopicQ.where(
          { id: SessionQ.topic_id, includeInactive: true },
          SQL`NOT (${TopicQ.name} = ANY(${TOPICS_TO_OMIT_FROM_PEER_REVIEWS}))`
        )})
      ) AND
      ${SessionQ.isAfterPeerReviewSystemLaunch()}
    )`,

  needsAuditAndCanBeAuditedByUser: (user: UserContextType): SQLFragment<boolean> =>
    SQL`(
      (NOT EXISTS(SELECT 1 ${TutorAuditQ.where({ auditorId: user.id, sessionId: SessionQ.id })})) AND
      (
        (NOT ${SessionQ.anyTutorIsHostingFutureSession()})
        OR ${SessionQ.start_date} > NOW() - ${PEER_REVIEW_RECENT_SESSION_THRESHOLD_DAYS} * INTERVAL '1 DAY'
      ) AND
      ${SessionQ.hasRelevantAuditorTagForUser(user)} AND
      ${SessionQ.userCanViewSession(user.id)} AND
      (NOT ${SessionQ.isHost({ userId: user.id })}) AND
      ${
        user.tutorAuditorLevel === TutorAuditorLevel.Novice
          ? SessionQ.needsAudit()
          : SessionQ.needsExperiencedAudit()
      }
    )`,

  isPubliclyListedForPeerReview: (): SQLFragment<boolean> =>
    SQL`COALESCE(
          (SELECT ${SeriesTemplateQ.peer_review_visibility} ${SeriesQ.where({
            id: SessionQ.series_id,
            include: [
              {
                association: 'SeriesTemplate',
                required: false,
              },
            ],
          })}),
          'public')
        = 'public'`,

  countRatings: (rating: TutorSessionFeedbackRating): SQLFragment<number> =>
    SQL`COUNT(*) FROM ${SessionFeedbackQ} WHERE ${SessionFeedbackQ.session_id} = ${SessionQ.id}
            AND ${SessionFeedbackQ.rating} = ${rating}
            AND ${SessionFeedbackQ.type} = ${SESSION_FEEDBACK_TYPE_TUTOR}`,

  hasAutomatedFeedback: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionFeedbackAutomatedQ.where({
      sessionId: SessionQ.id,
      ready: true,
    })})`,

  countAllRatings: (): SQLFragment<number> =>
    SQL`COUNT(*) FROM ${SessionFeedbackQ} WHERE ${SessionFeedbackQ.session_id} = ${SessionQ.id}
            AND ${SessionFeedbackQ.type} = ${SESSION_FEEDBACK_TYPE_TUTOR}`,

  countRatingsWithComments: (): SQLFragment<number> =>
    SQL`COUNT(*) FROM ${SessionFeedbackQ} WHERE ${SessionFeedbackQ.session_id} = ${SessionQ.id}
            AND length(${SessionFeedbackQ.comment}) > 0
            AND ${SessionFeedbackQ.type} = ${SESSION_FEEDBACK_TYPE_TUTOR}`,

  countRatingsWithNPS: (): SQLFragment<number> =>
    SQL`COUNT(*) ${SessionFeedbackQ.where(
      { sessionId: SessionQ.id, type: SESSION_FEEDBACK_TYPE_TUTOR },
      SQL`${SessionFeedbackQ.nps} IS NOT NULL`
    )}`,

  countAttemptedJoining: (): SQLFragment<number> =>
    SQL`COUNT(${SessionRegistrationQ.id}) ${SessionRegistrationQ.where({
      sessionId: SessionQ.id,
      attemptedJoining: true,
    })}`,

  // null means no data
  // number means we have data
  countZoomAttendees: (): SQLFragment<number | null> =>
    SQL`COUNT(DISTINCT ${SessionAttendeeQ.user_id}) ${SessionQ.getZoomAttendees()}`,

  getZoomAttendees: (): SQLFragment<number[]> =>
    SessionAttendeeQ.where(
      { sessionId: SessionQ.id },
      SQL`${SessionAttendeeQ.role} != ${SessionAttendeeRole.Tutor}`
    ),

  hasAtLeastOneLearner: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionAttendeeQ.where({
      sessionId: SessionQ.id,
      roles: [SessionAttendeeRole.Learner],
    })})`,

  hasUserSelfReservedSeries: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
    (${SessionQ.series_id} IS NOT NULL
      AND EXISTS(SELECT 1 ${SeriesReservationQ.where(
        {
          userId,
          reserverUserId: userId,
          seriesId: SessionQ.series_id,
        },
        SeriesReservationQ.isActive()
      )})
    )
  `,

  hasUserRegistered: (userId: number | null | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionRegistrationQ.where({
      sessionId: SessionQ.id,
      userId,
    })})`,

  isUserDropIn: (userId: number | null | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionRegistrationQ.where({
      sessionId: SessionQ.id,
      userId,
      type: 'series-dropin',
    })})`,

  didUserAttend: (userId: number | null | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS (SELECT 1 ${SessionAttendeeQ.where({ sessionId: SessionQ.id, userId })})`,

  // Note we are using raw sql instead of QueryBuilder here because the session registration query is scoped
  // to exclude records with session_breakout_id not null
  didUserAttemptJoining: (userId: number | null | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS (
      SELECT 1 FROM ${SessionRegistrationQ}
      WHERE ${SessionRegistrationQ.session_id} = ${SessionQ.id}
        AND ${SessionRegistrationQ.user_id} = ${userId}
        AND ${SessionRegistrationQ.attempted_joining} = true
        AND ${SessionRegistrationQ.deleted_at} IS NULL
    )`,

  canUserAccessAssessments: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`${SessionQ.hasUserRegistered(userId)} OR
        ${SessionQ.hasUserRegisteredForSeries(userId)} OR
        ${SessionQ.isHost({ userId })}`,

  hasUserRegisteredForSeries: (userId: number | null | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`(
      ${SessionQ.series_id} IS NOT NULL AND
      EXISTS(SELECT 1 ${SeriesRegistrationQ.where({
        seriesId: SessionQ.series_id,
        userId,
      })})
    )`,

  getSessionTemplate: (): SQLFragment<SessionTemplateAttributes> =>
    SessionTemplateQ.where({
      id: SessionQ.session_template_id,
    }),

  isHost: ({
    userId,
    approved = true,
    attemptedHosting,
  }: {
    userId: number | SQLFragment<number>
    // approved: null means we don't care about approval status
    approved?: boolean | null
    attemptedHosting?: boolean
  }): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionHostQ.where({
      sessionId: SessionQ.id,
      userId,
      approved,
      attemptedHosting,
    })})`,

  countSessionComments: (): SQLFragment<number> =>
    SQL`count(*)::integer ${SessionCommentQ.where({
      sessionId: SessionQ.id,
    })}`,

  countSessions: ({
    earliestDate,
    latestDate,
  }: {
    earliestDate?: SQLFragment<Date>
    latestDate?: SQLFragment<Date>
  }): SQLFragment<number> =>
    SQL`COUNT(DISTINCT ${SessionQ.id})::int
    ${SessionQ.where(
      {},
      SQL`
        ${earliestDate != null ? SQL`${SessionQ.start_date} >= ${earliestDate}` : SQL`TRUE`} AND
        ${latestDate != null ? SQL`${SessionQ.start_date} <= ${latestDate}` : SQL`TRUE`}`
    )}`,

  getTopic: (): SQLFragment<TopicAttributes> =>
    TopicQ.where({
      id: SessionQ.topic_id,
      includeInactive: true,
    }),

  getWorld: (): SQLFragment<TopicAttributes> =>
    WorldQ.where({
      id: SessionQ.world_id,
    }),

  getSeries: (): SQLFragment<SeriesAttributes> =>
    SQL`${SeriesQ.where({ id: SessionQ.series_id, includeAllResearchGroups: true })}`,
  getTutor: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: SessionQ.user_id,
      paranoid: false,
    }),
  getHosts: ({
    approved = true,
    role,
    attemptedHosting,
  }: {
    approved?: boolean
    role?: HostRole | null
    attemptedHosting?: boolean
  }): SQLFragment<SessionHostAttributes & UserAttributes> =>
    SessionHostQ.where({
      sessionId: SessionQ.id,
      approved,
      role,
      attemptedHosting,
      include: [{ association: 'User', required: true }],
    }),

  // Returns the conversation partners in breakout sessions for the given user in this session.
  getConversationPartners: (
    currentUserId: number | SQLFragment<number>
  ): SQLFragment<UserAttributes> =>
    SQL`${UserQ.where(
      {},
      SQL`${UserQ.id} IN (
        SELECT DISTINCT b.user_id
        FROM ${SessionRegistrationQ} a
        JOIN ${SessionRegistrationQ} b
          ON a.session_id = b.session_id
         AND a.session_breakout_id = b.session_breakout_id
         AND b.user_id != a.user_id
        WHERE a.session_id = ${SessionQ.id}
          AND a.user_id = ${currentUserId}
          AND a.session_breakout_id IS NOT NULL
          AND a.attempted_joining IS TRUE
          AND b.attempted_joining IS TRUE
      )`
    )}`,

  // Returns the number of conversation partners in breakout sessions for the given user in this session.
  countConversationPartners: (
    currentUserId: number | SQLFragment<number>
  ): SQLFragment<number | null> => SQL`COUNT(*) ${SessionQ.getConversationPartners(currentUserId)}`,

  // Position starts at 1, not zero! It's not index
  getPositionInSeries: ({
    excludeNonTemplatedSessions = false,
  }: {
    excludeNonTemplatedSessions?: boolean
  } = {}): SQLFragment<number> =>
    SQL`COUNT(*) FROM ${SessionQ} s2 WHERE
      s2.deleted_at IS NULL
      AND s2.series_id = ${SessionQ.series_id}
      AND s2.start_date <= ${SessionQ.start_date}
      AND ${excludeNonTemplatedSessions ? SQL`s2.session_template_id IS NOT NULL` : SQL`TRUE`}`,

  countAllHosts: ({ approved }: { approved: boolean }): SQLFragment<number> =>
    SQL`COUNT(*) ${SessionHostQ.where({ sessionId: SessionQ.id, approved })}`,

  getSessionTutorSessionFeedback: (): SQLFragment<
    TutorSessionFeedbackAttributes[]
  > => SQL`FROM ${SessionFeedbackQ}
        WHERE ${SessionFeedbackQ.session_id} = ${SessionQ.id} AND ${SessionFeedbackQ.type} = ${SESSION_FEEDBACK_TYPE_TUTOR} ORDER BY ${SessionFeedbackQ.created_at} DESC`,

  getSessionLearnerFeedbackWhere: ({
    userId,
    types,
  }: {
    userId: number
    types?: LearnerFeedbackType[]
  }): SQLFragment<boolean> =>
    SQL`${LearnerFeedbackQ.session_id} = ${SessionQ.id}
      AND ${LearnerFeedbackQ.reviewee_user_id} = ${userId}
      AND ${LearnerFeedbackQ.reviewer_user_id} IS NOT NULL
      AND ${types != null ? SQL`${LearnerFeedbackQ.type} = ANY(${types})` : SQL`TRUE`}
      AND ${LearnerFeedbackQ.deleted_at} IS NULL
  `,

  getSessionLearnerFeedback: ({
    userId,
    types,
  }: {
    userId: number
    types?: LearnerFeedbackType[]
  }): SQLFragment<LearnerFeedbackAttributes> =>
    SQL`FROM ${LearnerFeedbackQ}
      WHERE ${SessionQ.getSessionLearnerFeedbackWhere({ userId, types })}
      ORDER BY ${LearnerFeedbackQ.selfReflectionFilter(true)} DESC`,

  countSessionLearnerFeedback: ({
    userId,
    types,
  }: {
    userId: number
    types?: LearnerFeedbackType[]
  }): SQLFragment<number> => SQL`
    COUNT(*) FROM ${LearnerFeedbackQ}
      WHERE ${SessionQ.getSessionLearnerFeedbackWhere({ userId, types })}
  `,

  getSessionAudits: (): SQLFragment<TutorAuditAttributes> =>
    TutorAuditQ.where({ sessionId: SessionQ.id, order: [[TutorAuditQ.created_at, 'desc']] }),

  countAllAudits: ({
    isUsingAuditorRubricVersion = false,
    onlyShowExperiencedAudits = false,
  }: {
    isUsingAuditorRubricVersion?: boolean
    onlyShowExperiencedAudits?: boolean
  }): SQLFragment<number> =>
    SQL`count(*) ${TutorAuditQ.where(
      { sessionId: SessionQ.id, isUsingAuditorRubricVersion },
      onlyShowExperiencedAudits ? SQL`${TutorAuditQ.isExperienced()}` : SQL`TRUE`
    )}`,

  getComputedStartDate: (): SQLFragment<string> => SQL`
        COALESCE(${SessionQ.zoom_start_date}, ${SessionQ.start_date})
  `,

  getComputedEndDate: (): SQLFragment<string> => SQL`
        COALESCE(${SessionQ.zoom_end_date}, ${SessionQ.end_date})
    `,

  isInProgress: (): SQLFragment<boolean> =>
    SQL`${SessionQ.getComputedStartDate()} <= NOW() AND ${SessionQ.getComputedEndDate()} > NOW()`,

  anyTutorIsHostingFutureSession: (): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT 1 FROM ${SessionHostQ} sh
        JOIN ${SessionQ} s ON s.id = sh.session_id
        JOIN ${SessionHostQ} sh2 ON sh2.session_id = ${SessionQ.id} AND sh2.user_id = sh.user_id
      WHERE s.start_date > NOW()
        AND sh.user_id = sh2.user_id
        AND s.deleted_at IS NULL
        AND sh2.deleted_at IS NULL
    )`,

  hasAssociatedSafetyReport: (): SQLFragment<boolean> =>
    SQL`(EXISTS (SELECT 1 ${PlatformFeedbackQ.where({ sessionId: SessionQ.id })}))`,

  isOneOnOne: (sessionId: number | SQLFragment<number>): SQLFragment<LearnerFeedbackAttributes> =>
    SQL`(SELECT
          COUNT(DISTINCT ${SessionAttendeeQ.user_id})
            FILTER (WHERE ${SessionAttendeeQ.role} = ${SessionAttendeeRole.Learner}) = 1
      AND COUNT(DISTINCT ${SessionAttendeeQ.user_id})
            FILTER (WHERE ${SessionAttendeeQ.role} = ${SessionAttendeeRole.OnboardingBuddy}) = 0
      ${SessionAttendeeQ.where({
        roles: [SessionAttendeeRole.Learner, SessionAttendeeRole.OnboardingBuddy],
        sessionId,
      })}
    )`,

  getDisplayTitle: (): SQLFragment<string> =>
    SQL`(CASE
      WHEN ${SessionQ.title} IS NOT NULL THEN ${SessionQ.title} ELSE
      ${TopicQ.name}
    END)`,

  isSATSeriesSession: (): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${SATSeriesQ.where({ seriesId: SessionQ.series_id })})`,

  isDialoguesSeriesSession: (): SQLFragment<boolean> => SQL`
    (SELECT ${SeriesQ.isDialoguesSeries()} ${SeriesQ.where({ id: SessionQ.series_id })})`,

  getSessionFeedbackType: (): SQLFragment<SessionFeedbackType> => SQL`
    CASE
      WHEN ${SessionQ.isDialoguesSeriesSession()} THEN ${SESSION_FEEDBACK_TYPE_DIALOGUES}
      ELSE ${SESSION_FEEDBACK_TYPE_TUTOR}
    END
  `,

  isWithinZoomExpirationPeriod: (): SQLFragment<boolean> =>
    SQL`${SessionQ.start_date} > NOW() - ${ZOOM_DATA_RETENTION_DAYS} * INTERVAL '1 DAY'`,

  isWorldVisibility: (visibility: WorldVisibility): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${WorldQ.where({ visibility, id: SessionQ.world_id })})
  `,

  isInWorld: (slug: string): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${WorldQ.where({ id: SessionQ.world_id, slug })})
  `,

  userCanViewSession: (
    userId: number | SQLFragment<number>,
    {
      disableBucketCheck = false,
    }: {
      disableBucketCheck?: boolean
    } = {}
  ): SQLFragment<boolean> =>
    SQL`(
    ${SessionQ.userCanViewWorldSession(userId)}
    AND ${
      disableBucketCheck
        ? SQL`TRUE`
        : SQL`(
            ${UserQ.isBucketCompatible({ myUserId: userId, targetUserId: SessionQ.user_id })}
            OR ${
              // We allow users in different buckets to co-host, so they should be able to
              // view their own session so long as they have been approved.
              SessionQ.isHost({ userId, approved: true })
            }
          )`
    })`,

  /**
   * @deprecated: Use userCanViewWorldSession instead
   */
  userCanViewWorldSession: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
    (
      ${SessionQ.world_id} IS NULL OR
      ${SessionQ.isWorldVisibility('public')} OR
      EXISTS(SELECT 1 ${WorldMemberQ.where({
        worldId: SessionQ.world_id,
        userId,
      })})
    )`,

  userMatchesSessionResearchGroup: (user: UserContextType): SQLFragment<boolean> => SQL`(
    ${SessionQ.series_id} IS NULL
    OR (
      SELECT ${SeriesQ.userMatchesSeriesResearchGroup(user)}
      ${SeriesQ.where({ id: SessionQ.series_id, includeAllResearchGroups: true })}
    )
  )`,

  inResearchGroupSeries: (): SQLFragment<boolean> => SQL`
      EXISTS(SELECT 1 ${SeriesQ.where(
        {
          id: SessionQ.series_id,
          includeAllResearchGroups: true,
        },
        SQL`${SeriesQ.getResearchGroup()} IS NOT NULL`
      )})
  `,

  getGatingGroup: ({
    skipSeriesInclude = false,
  }: {
    skipSeriesInclude?: boolean
  } = {}): SQLFragment<GatingGroup | null> => {
    if (skipSeriesInclude) {
      return SQL`COALESCE(${SessionQ.internal_state}->>'gating_group', ${SeriesQ.internal_state}->>'gating_group')`
    }
    return SQL`COALESCE(${SessionQ.internal_state}->>'gating_group', (SELECT ${
      SeriesQ.internal_state
    }->>'gating_group' ${SessionQ.getSeries()}))`
  },

  hasGatingGroup: (group: GatingGroup): SQLFragment<boolean> =>
    SQL`${SessionQ.getGatingGroup()} = ${group}`,

  needsMentor: (): SQLFragment<boolean> => SQL`
    (
      (${SessionQ.hasMentorTag()}) OR
      ${SessionQ.getMentorStatus()} = ${SessionMentorStatus.NeedsMentor}
    )
    AND (${SessionQ.getMentorStatus()} != ${SessionMentorStatus.InProgress})
    AND (${SessionQ.getMentorStatus()} != ${SessionMentorStatus.Resolved})
    AND (${SessionQ.isAfterPeerReviewSystemLaunch()})
  `,

  isAfterPeerReviewSystemLaunch: (): SQLFragment<boolean> =>
    SQL`${SessionQ.start_date} > date '2022-06-25'`,

  countExcusedAbsencesSubmitted: (): SQLFragment<number> =>
    SQL`COUNT(*) ${SessionRegistrationQ.where(
      { sessionId: SQL`${SessionQ.id}` },
      SQL`${SessionRegistrationQ.excused_absence_reason} IS NOT NULL`
    )}`,

  hasSATBootcampLessonPrepResponse: (): SQLFragment<boolean> =>
    SQL`${SessionQ.lesson_prep_response_at} IS NOT NULL`,

  getTotalTutorSpeakingPercentage: (): SQLFragment<number> =>
    SQL`(
        SELECT ${SessionAttendeeQ.getTotalTutorSpeakingPercentage()}
        ${SessionAttendeeQ.where(
          {
            sessionId: SessionQ.id,
            roles: [SessionAttendeeRole.Tutor, SessionAttendeeRole.Learner],
          },
          SQL`${SessionAttendeeQ.speaking_duration_seconds} IS NOT NULL`
        )}
    )`,

  happenedBeforeTalkTimeMigration: (): SQLFragment<boolean> =>
    SQL`${SessionQ.start_date} < '2023-01-01'`,

  getHighlyEngagingSessions: ({
    userId,
    worldSlug,
  }: {
    userId: number | SQLFragment<number>
    worldSlug: string
  }): SQLFragment<SessionAttributes> =>
    SessionTagQ.where({
      tag: SessionTagType.HighlyEngagingSession,
      include: [
        {
          association: 'Session',
          required: true,
          where: SessionQ.isHost({ userId, attemptedHosting: true }),
          include: [
            {
              association: 'World',
              required: true,
              where: SQL`${WorldQ.slug} = ${worldSlug}`,
            },
          ],
        },
      ],
    }),

  getHostedSessions: ({
    userId,
    worldSlug,
  }: {
    userId: number | SQLFragment<number>
    worldSlug: string
  }): SQLFragment<SessionAttributes> =>
    SessionQ.where(
      {
        include: [
          {
            association: 'World',
            required: true,
            where: SQL`${WorldQ.slug} = ${worldSlug}`,
          },
        ],
      },
      SessionQ.isHost({ userId, attemptedHosting: true })
    ),

  getAcademicSessionsHostedBy: (
    userId: number | SQLFragment<number>
  ): SQLFragment<SessionAttributes> =>
    SessionQ.where({
      tutor: userId,
      include: [
        {
          association: 'World',
          required: true,
          where: WorldQ.isAcademic(),
        },
      ],
    }),

  // Excludes enrichment sessions from pre-subworld-launch (world_id is null) so
  // they don't retroactively count for VH
  isEligibleForVolunteerHours: (): SQLFragment<boolean> => SQL`(
    ${SessionQ.getComputedEndDate()} <= NOW() AND
    (
      SELECT (
        (${TopicQ.name} != ALL(${EXCLUDED_SESSION_TOPICS})) AND
        (
          (${SessionQ.world_id} IS NULL) OR
          (${WorldQ.slug} != ALL(${EXCLUDED_WORLD_SLUGS}))
        ) AND
        NOT (${TopicQ.name} = ${ENRICHMENT_TOPIC} AND ${SessionQ.world_id} IS NULL)
      )
      FROM ${TopicQ}
      LEFT JOIN ${WorldQ} ON ${WorldQ.id} = ${SessionQ.world_id}
      WHERE
        ${TopicQ.deleted_at} IS NULL AND
        ${WorldQ.deleted_at} IS NULL AND
        ${TopicQ.id} = ${SessionQ.topic_id}
    )
  )`,

  getLearnerWithLongestAttendance: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: SQL`
        (SELECT ${SessionAttendeeQ.user_id} ${SessionAttendeeQ.where({
          sessionId: SessionQ.id,
          roles: [SessionAttendeeRole.Learner],
          groupBy: [SessionAttendeeQ.user_id],
          order: [[SQL`SUM(${SessionAttendeeQ.duration_seconds})`, 'desc']],
          limit: 1,
        })})
      `,
    }),

  getLongestLearnerAttendanceSeconds: (): SQLFragment<number> =>
    SQL`
        (SELECT SUM(${SessionAttendeeQ.duration_seconds}) ${SessionAttendeeQ.where({
          sessionId: SessionQ.id,
          roles: [SessionAttendeeRole.Learner],
          groupBy: [SessionAttendeeQ.user_id],
          order: [[SQL`SUM(${SessionAttendeeQ.duration_seconds})`, 'desc']],
          limit: 1,
        })})
      `,

  getViewpointSettings: (): SQLFragment<ViewpointSettings | null> =>
    SQL`${SessionQ.internal_state}->'viewpoint_settings'`,

  getDialoguesAttended: ({
    userId,
    beforeBreakoutSessionLaunch,
    customWhere,
  }: {
    userId: number | SQLFragment<number>
    beforeBreakoutSessionLaunch?: boolean
    customWhere?: SQLFragment<boolean>
  }): SQLFragment<unknown> => {
    let dateCondition: SQLFragment<boolean> = SQL`TRUE`

    if (beforeBreakoutSessionLaunch != null) {
      dateCondition = beforeBreakoutSessionLaunch
        ? SQL`${SessionQ.start_date} < ${BREAKOUT_ROOM_START_DATE}`
        : SQL`${SessionQ.start_date} >= ${BREAKOUT_ROOM_START_DATE}`
    }

    const finalCondition =
      customWhere == null ? dateCondition : SQL`(${dateCondition}) AND (${customWhere})`

    return SQL`${SessionQ.where(
      {
        include: [
          {
            association: 'SessionAttendees',
            required: true,
            where: SQL`${SessionAttendeeQ.user_id} = ${userId}`,
          },
          {
            association: 'World',
            required: true,
            where: SQL`${WorldQ.slug} = ${DIALOGUES_WORLD_SLUG}`,
          },
          {
            association: 'Series',
            required: false,
          },
        ],
      },
      finalCondition
    )}`
  },

  countDialoguesAttended: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<number> =>
    SQL`COUNT(DISTINCT ${SessionQ.id}) ${SessionQ.getDialoguesAttended({
      userId,
    })}`,

  hasBeenHostedBy: (
    {
      userId,
      requireLearnerAttendance,
    }: {
      userId: number | SQLFragment<number>
      requireLearnerAttendance: boolean
    },
    customWhere?: SQLFragment<boolean>
  ): SQLFragment<boolean> =>
    SQL`(
      ${SessionQ.id} IN (SELECT ${SessionQ.id} ${UserQ.getTutoringSessions({
        userId,
        requireLearnerAttendance,
        customWhere,
      })})
    )`,

  hasBeenAttendedBy: (
    { userId }: { userId: number | SQLFragment<number> },
    customWhere?: SQLFragment<boolean>
  ): SQLFragment<boolean> =>
    SQL`(
      ${SessionQ.id} IN (SELECT ${SessionQ.id} ${UserQ.getAttendedSessions({ userId, customWhere })})
    )`,

  getSessionFeedbackAutomated: (): SQLFragment<SessionFeedbackAutomatedAttributes> =>
    SessionFeedbackAutomatedQ.where({ sessionId: SessionQ.id }),

  getQualityMetricPayload<T extends SessionQualityMetricSlug>(
    slug: T
  ): SQLFragment<SessionQualityMetricSlugToPayload[T]> {
    return SQL`${SessionQ.quality_metrics}->'metrics'->${slug}`
  },

  isAttendanceRequired(): SQLFragment<boolean> {
    return SQL`${SessionQ.attendance_policy} = 'required'`
  },

  countLearnerFeedbacks: ({
    revieweeUserId,
    sessionId,
    types,
  }: {
    revieweeUserId: number | SQLFragment<number>
    sessionId: number | SQLFragment<number> | boolean
    types: LearnerFeedbackType[]
  }): SQLFragment<number> => {
    const sessionCondition =
      sessionId === true
        ? SQL`${LearnerFeedbackQ.session_id} IS NOT NULL`
        : sessionId === false
          ? SQL`${LearnerFeedbackQ.session_id} IS NULL`
          : SQL`${LearnerFeedbackQ.session_id} = ${sessionId}`

    return SQL`COUNT(*) FROM ${LearnerFeedbackQ}
    WHERE ${sessionCondition}
      AND ${LearnerFeedbackQ.reviewee_user_id} = ${revieweeUserId}
      AND ${LearnerFeedbackQ.type} = ANY(${types})`
  },

  /**
   * Note: This filter should match the logic in UPCOMING_SESSIONS_FILTER_BASE
   * defined in server/services/session/upcoming/constants.ts. If you change this,
   * update that definition as well to keep them in sync.
   */
  upcomingSessionsFilter: (): SQLFragment<boolean> =>
    SQL`${SessionQ.end_date} > NOW() AND (${SessionQ.zoom_end_date} IS NULL OR ${SessionQ.zoom_end_date} < ${SessionQ.start_date}) AND ${SessionQ.attempted_hosting} IS FALSE`,
}

export default SessionQ
